import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { formatDate } from "../../lib/date";
import {
  FileText, Search, Plus, Download, Upload, Pencil, Trash2, Loader2, X,
  History, ShieldCheck, Clock, AlertTriangle, Building2, User, Tag, Sparkles, CornerDownLeft, Lock,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_CATEGORIES = [
  { id: "policy", label: "Policies" },
  { id: "sop", label: "Procedures" },
  { id: "form", label: "Forms" },
  { id: "other", label: "Other" },
];
const FALLBACK_DEPARTMENTS = [
  "Executive", "Operations", "Sales", "Marketing", "Customer Success", "Support",
  "Product", "Finance", "HR", "IT", "Legal & Compliance", "WHS / Safety",
];

function groupByCategory(docs, catOrder, catLabel) {
  const groups = catOrder
    .map((cid) => ({ cid, label: catLabel[cid], items: docs.filter((d) => d.category === cid) }))
    .filter((g) => g.items.length > 0);
  const known = new Set(catOrder);
  const extra = docs.filter((d) => !known.has(d.category));
  if (extra.length) groups.push({ cid: "_other", label: "Uncategorised", items: extra });
  return groups;
}
const STATUSES = [
  { id: "draft", label: "Draft", tone: "neutral" },
  { id: "published", label: "Published", tone: "success" },
  { id: "under_review", label: "Under review", tone: "warning" },
  { id: "archived", label: "Archived", tone: "neutral" },
];
const STATUS_TONE = Object.fromEntries(STATUSES.map((s) => [s.id, s.tone]));
const STATUS_LABEL = Object.fromEntries(STATUSES.map((s) => [s.id, s.label]));

function bytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function DocumentRegistry() {
  const [docs, setDocs] = useState([]);
  const [cats, setCats] = useState(FALLBACK_CATEGORIES);
  const [depts, setDepts] = useState(FALLBACK_DEPARTMENTS);
  const catLabel = Object.fromEntries(cats.map((c) => [c.id, c.label]));
  const catOrder = cats.map((c) => c.id);
  const [stats, setStats] = useState({ total: 0, review_overdue: 0, review_due_soon: 0 });
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [detail, setDetail] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/lookups/document_category`).catch(() => null),
      axios.get(`${API}/lookups/document_department`).catch(() => null),
    ]).then(([c, d]) => {
      if (c?.data?.items?.length) setCats(c.data.items.map((i) => ({ id: i.key, label: i.label })));
      if (d?.data?.items?.length) setDepts(d.data.items.map((i) => i.label));
    });
  }, []);

  const [editDoc, setEditDoc] = useState(null);
  const [versionFor, setVersionFor] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [askQ, setAskQ] = useState("");
  const [asking, setAsking] = useState(false);
  const [thread, setThread] = useState([]);
  const [sessionId, setSessionId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (query) params.q = query;
      if (category !== "all") params.category = category;
      if (status !== "all") params.status = status;
      const r = await axios.get(`${API}/documents`, { params });
      setDocs(r.data.documents || []);
      setStats(r.data.stats || { total: 0, review_overdue: 0, review_due_soon: 0 });
      setCanManage(!!r.data.can_manage);
    } catch (e) { toast.error("Failed to load documents"); }
    finally { setLoading(false); }
  }, [query, category, status]);
  useEffect(() => { const t = setTimeout(load, query ? 300 : 0); return () => clearTimeout(t); }, [load, query]);

  const openDetail = async (d) => {
    try {
      const r = await axios.get(`${API}/documents/${d.id}`);
      setDetail(r.data);
    } catch (e) { toast.error("Failed to open document"); }
  };

  const ask = async () => {
    const question = askQ.trim();
    if (!question || asking) return;
    setAsking(true);
    setThread((t) => [...t, { role: "user", content: question }]);
    setAskQ("");
    try {
      const r = await axios.post(`${API}/documents/ask`, { question, session_id: sessionId });
      setSessionId(r.data.session_id);
      setThread((t) => [...t, { role: "assistant", content: r.data.answer, citations: r.data.citations || [] }]);
    } catch (e) {
      const msg = e?.response?.data?.detail || "AI assistant unavailable";
      toast.error(msg);
      setThread((t) => [...t, { role: "assistant", content: msg, citations: [], error: true }]);
    }
    finally { setAsking(false); }
  };
  const newChat = () => { setThread([]); setSessionId(null); setAskQ(""); };
  const refreshDetail = async (id) => {
    try { const r = await axios.get(`${API}/documents/${id}`); setDetail(r.data); } catch (e) {}
  };

  const download = async (d, version) => {
    try {
      const params = version ? { version } : {};
      const res = await axios.get(`${API}/documents/${d.id}/download`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = (version ? `v${version}_` : "") + (d.current_filename || `${d.doc_ref}`);
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Failed to download"); }
  };

  const del = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/documents/${deleteTarget.id}`);
      toast.success("Document deleted");
      setDeleteTarget(null); setDetail(null); load();
    } catch (e) { toast.error("Failed to delete"); }
    finally { setDeleting(false); }
  };

  const changeStatus = async (d, s) => {
    try {
      await axios.post(`${API}/documents/${d.id}/status`, { status: s });
      toast.success(`Status → ${STATUS_LABEL[s]}`);
      await refreshDetail(d.id); load();
    } catch (e) { toast.error("Failed to update status"); }
  };

  return (
    <div className="space-y-4" data-testid="document-registry">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#FFF8D6] flex items-center justify-center"><FileText className="w-5 h-5 text-[#8A6D00]" /></div>
          <div>
            <h2 className="heading text-2xl text-[#1A1A1A] tracking-tight">Documents</h2>
            <p className="text-sm text-[#59554D]">Corporate policies, SOPs, forms and templates — with version history.</p>
          </div>
        </div>
        {canManage && (
          <Button onClick={() => { setEditDoc(null); setFormOpen(true); }} className="h-9 bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] uppercase text-[11px] tracking-wide font-semibold" data-testid="doc-new-btn"><Plus className="w-4 h-4 mr-2" /> New document</Button>
        )}
      </div>

      {/* AI Document Agent */}
      <Card className="bg-[#FFFCF2] border-[#ECE1C2] shadow-card rounded-xl overflow-hidden" data-testid="doc-ai-panel">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2.5">
            <Sparkles className="w-4 h-4 text-[#B8860B]" />
            <span className="text-[13px] font-semibold text-[#1A1A1A]">Ask the document assistant</span>
            {thread.length === 0 && <span className="text-[11px] text-[#8A857A]">e.g. "where is our core marketing one-pager?"</span>}
            {thread.length > 0 && (
              <button onClick={newChat} className="ml-auto text-[11px] text-[#8A857A] hover:text-[#1A1A1A] flex items-center gap-1" data-testid="doc-ask-newchat"><X className="w-3 h-3" /> New chat</button>
            )}
          </div>

          {thread.length > 0 && (
            <div className="mb-3 space-y-2.5 max-h-[340px] overflow-y-auto pr-1" data-testid="doc-ask-thread">
              {thread.map((m, i) => (
                m.role === "user" ? (
                  <div key={i} className="flex justify-end">
                    <div className="bg-[#FFD108] text-[#1A1A1A] rounded-2xl rounded-br-sm px-3.5 py-2 text-[13px] font-medium max-w-[80%]" data-testid={`doc-ask-user-${i}`}>{m.content}</div>
                  </div>
                ) : (
                  <div key={i} className="flex justify-start">
                    <div className="bg-white border border-[#E6E2D9] rounded-2xl rounded-bl-sm px-3.5 py-2.5 max-w-[88%] shadow-sm" data-testid={`doc-ask-assistant-${i}`}>
                      <p className={`text-[13.5px] leading-relaxed whitespace-pre-wrap ${m.error ? "text-[#C23A00]" : "text-[#2A2A2A]"}`}>{m.content}</p>
                      {m.citations?.length > 0 && (
                        <div className="mt-2 space-y-1.5">
                          {m.citations.map((c) => (
                            <button key={c.id} onClick={() => openDetail({ id: c.id })} className="w-full text-left flex items-start gap-2.5 rounded-md bg-[#FBFAF7] border border-[#E6E2D9] hover:border-[#FFD108] px-3 py-2 transition-colors" data-testid={`doc-ask-citation-${c.doc_ref}`}>
                              <FileText className="w-3.5 h-3.5 text-[#B8860B] mt-0.5 shrink-0" />
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="mono text-[11px] font-semibold text-[#1A1A1A]">{c.doc_ref}</span>
                                  <span className="text-[12px] text-[#59554D] truncate">{c.title}</span>
                                </div>
                                {c.snippet && <div className="text-[11px] text-[#8A857A] truncate mt-0.5">{c.snippet}</div>}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )
              ))}
              {asking && (
                <div className="flex justify-start"><div className="bg-white border border-[#E6E2D9] rounded-2xl rounded-bl-sm px-3.5 py-2.5 flex items-center gap-2 text-[#8A857A] text-[13px]"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching your documents…</div></div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Input
                value={askQ}
                onChange={(e) => setAskQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !asking) ask(); }}
                placeholder={thread.length ? "Ask a follow-up…" : "Ask a question about your documents…"}
                className="bg-white border-[#DAD5CA] text-[#1A1A1A] placeholder:text-[#8A857A] pr-9"
                data-testid="doc-ask-input"
              />
              <CornerDownLeft className="w-3.5 h-3.5 text-[#B8B2A5] absolute right-3 top-1/2 -translate-y-1/2" />
            </div>
            <Button onClick={ask} disabled={asking || !askQ.trim()} className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] font-semibold" data-testid="doc-ask-btn">
              {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              <span className="ml-1.5">Ask</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="bg-white border-[#DAD5CA] shadow-card rounded-xl"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-wide text-[#8A857A] font-semibold">Documents</div><div className="text-2xl font-bold text-[#1A1A1A] mt-1" data-testid="doc-stat-total">{stats.total}</div></CardContent></Card>
        <Card className="bg-white border-[#DAD5CA] shadow-card rounded-xl"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-wide text-[#8A857A] font-semibold">Review overdue</div><div className={`text-2xl font-bold mt-1 ${stats.review_overdue ? "text-[#C23A00]" : "text-[#1A1A1A]"}`} data-testid="doc-stat-overdue">{stats.review_overdue}</div></CardContent></Card>
        <Card className="bg-white border-[#DAD5CA] shadow-card rounded-xl"><CardContent className="p-4"><div className="text-[10px] uppercase tracking-wide text-[#8A857A] font-semibold">Review due soon</div><div className={`text-2xl font-bold mt-1 ${stats.review_due_soon ? "text-[#8A6D00]" : "text-[#1A1A1A]"}`} data-testid="doc-stat-duesoon">{stats.review_due_soon}</div></CardContent></Card>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-[#8A857A] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search title, ref, tag" className="pl-9 bg-white border-[#DAD5CA]" data-testid="doc-search" />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {["all", ...cats.map((c) => c.id)].map((c) => (
            <button key={c} onClick={() => setCategory(c)} className={`h-8 px-3 rounded-full text-[11px] font-semibold uppercase tracking-wide transition-colors ${category === c ? "bg-[#1A1A1A] text-white" : "bg-white border border-[#DAD5CA] text-[#59554D] hover:bg-[#F4F2ED]"}`} data-testid={`doc-cat-${c}`}>{c === "all" ? "All" : catLabel[c]}</button>
          ))}
        </div>
      </div>
      {canManage && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] uppercase tracking-wide text-[#8A857A] font-semibold mr-1">Status</span>
          {["all", ...STATUSES.map((s) => s.id)].map((s) => (
            <button key={s} onClick={() => setStatus(s)} className={`h-7 px-2.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wide transition-colors ${status === s ? "bg-[#1A1A1A] text-white" : "bg-white border border-[#DAD5CA] text-[#59554D] hover:bg-[#F4F2ED]"}`} data-testid={`doc-status-filter-${s}`}>{s === "all" ? "All" : STATUS_LABEL[s]}</button>
          ))}
        </div>
      )}

      {/* List */}
      <Card className="bg-white border-[#DAD5CA] shadow-card rounded-xl overflow-hidden p-0">
        <CardContent className="p-0">
          <div className="grid items-center px-4 py-2.5 bg-[#F6F4EF] border-b border-[#DAD5CA] text-[10px] uppercase tracking-[0.09em] text-[#6B6660] font-medium" style={{ gridTemplateColumns: "110px 1fr 110px 150px 120px 110px 80px" }}>
            <span>Ref</span><span>Title</span><span>Category</span><span>Owner / Dept</span><span>Status</span><span>Updated</span><span className="text-right">Actions</span>
          </div>
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => <div key={i} className="px-4 py-3 border-b border-[#F0EDE6]"><div className="skeleton h-4 w-full" /></div>)
          ) : docs.length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-[#8A857A]" data-testid="doc-empty">No documents{query || category !== "all" || status !== "all" ? " match your filters" : " yet"}.</div>
          ) : groupByCategory(docs, catOrder, catLabel).map((g) => (
            <div key={g.cid} data-testid={`doc-group-${g.cid}`}>
              <div className="px-4 py-1.5 bg-[#F0ECE1] border-b border-[#E2DCCF] text-[10px] uppercase tracking-[0.1em] text-[#59554D] font-bold flex items-center gap-2">
                {g.label}<span className="text-[#A39D8F] font-medium">· {g.items.length}</span>
              </div>
              {g.items.map((d) => (
                <div key={d.id} onClick={() => openDetail(d)} className="grid items-center px-4 py-2.5 border-b border-[#F0EDE6] hover:bg-[#FFFCEF] cursor-pointer" style={{ gridTemplateColumns: "110px 1fr 110px 150px 120px 110px 80px" }} data-testid={`doc-row-${d.id}`}>
                  <span className="mono text-[11.5px] font-semibold text-[#1A1A1A]">{d.doc_ref}</span>
                  <div className="min-w-0 pr-3">
                    <div className="text-[13px] text-[#1A1A1A] truncate font-medium flex items-center gap-1.5">
                      {d.title}
                      {d.restricted && <Lock className="w-3 h-3 text-[#B8860B] shrink-0" title="Restricted access" />}
                    </div>
                    {d.match_snippet && (
                      <div className="text-[11px] text-[#8A6D00] truncate mt-0.5 flex items-center gap-1"><Search className="w-3 h-3 shrink-0" /> {d.match_snippet}</div>
                    )}
                    {d.review_state && (
                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold mt-0.5 ${d.review_state === "overdue" ? "text-[#C23A00]" : "text-[#8A6D00]"}`}>
                        <AlertTriangle className="w-3 h-3" /> Review {d.review_state === "overdue" ? "overdue" : "due soon"}
                      </span>
                    )}
                  </div>
                  <span className="text-[12px] text-[#59554D]">{catLabel[d.category] || d.category}</span>
                  <span className="text-[12px] text-[#59554D] truncate">{d.owner_name || d.department || "—"}</span>
                  <span className={`ds-pill ${STATUS_TONE[d.status] || "neutral"}`}><span className="dot" /> {STATUS_LABEL[d.status] || d.status}</span>
                  <span className="text-[11.5px] text-[#8A857A]">{d.updated_at ? formatDate(d.updated_at) : "—"}</span>
                  <div className="flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => download(d)} title="Download current" className="w-7 h-7 rounded-md flex items-center justify-center text-[#6B6660] hover:bg-[#F4F2ED] hover:text-[#1A1A1A]" data-testid={`doc-download-${d.id}`}><Download className="w-3.5 h-3.5" /></button>
                    {canManage && <button onClick={() => setDeleteTarget(d)} title="Delete" className="w-7 h-7 rounded-md flex items-center justify-center text-[#6B6660] hover:bg-[#FFE9E0] hover:text-[#C23A00]" data-testid={`doc-row-delete-${d.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
                    {d.current_version_no > 1 && <span className="mono text-[10px] text-[#8A857A]" title="Version">v{d.current_version_no}</span>}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Detail Sheet */}
      <Sheet open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <SheetContent side="right" className="bg-[#FBFAF7] border-[#DAD5CA] w-full sm:max-w-xl overflow-y-auto p-0" data-testid="doc-detail-sheet">
          {detail && (
            <div>
              <SheetHeader className="px-6 py-4 border-b border-[#E6E2D9] bg-white">
                <SheetTitle className="flex items-center gap-2 flex-wrap">
                  <span className="mono text-sm text-[#8A857A]">{detail.document.doc_ref}</span>
                  <span className={`ds-pill ${STATUS_TONE[detail.document.status] || "neutral"}`}><span className="dot" /> {STATUS_LABEL[detail.document.status]}</span>
                  {detail.document.review_state && <span className={`ds-pill ${detail.document.review_state === "overdue" ? "overdue" : "warning"}`}><span className="dot" /> Review {detail.document.review_state === "overdue" ? "overdue" : "due soon"}</span>}
                  {detail.document.restricted && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#B8860B] bg-[#FFF6DA] border border-[#EBD9A0] rounded-full px-2 py-0.5" data-testid="doc-detail-restricted-badge"><Lock className="w-3 h-3" /> Restricted</span>}
                </SheetTitle>
                <p className="text-[15px] font-semibold text-[#1A1A1A] text-left mt-1">{detail.document.title}</p>
                {detail.document.description && <p className="text-sm text-[#59554D] text-left whitespace-pre-wrap">{detail.document.description}</p>}
              </SheetHeader>

              {/* Actions */}
              <div className="px-6 py-3 flex flex-wrap gap-2 border-b border-[#E6E2D9] bg-white">
                <Button size="sm" onClick={() => download(detail.document)} className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A]" data-testid="doc-detail-download"><Download className="w-3.5 h-3.5 mr-1.5" /> Download</Button>
                {detail.can_manage && <>
                  <Button size="sm" variant="outline" onClick={() => { setVersionFor(detail.document); }} className="border-[#DAD5CA] text-[#59554D] hover:bg-[#F4F2ED]" data-testid="doc-detail-newversion"><Upload className="w-3.5 h-3.5 mr-1.5" /> New version</Button>
                  <Button size="sm" variant="outline" onClick={() => { setEditDoc(detail.document); setFormOpen(true); }} className="border-[#DAD5CA] text-[#59554D] hover:bg-[#F4F2ED]" data-testid="doc-detail-edit"><Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(detail.document)} className="text-[#C23A00] hover:bg-[#FFE9E0]" data-testid="doc-detail-delete"><Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete</Button>
                </>}
              </div>

              <div className="px-6 py-4 space-y-4">
                {/* Metadata */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <Meta icon={FileText} label="Category" value={catLabel[detail.document.category] || detail.document.category} />
                  <Meta icon={Building2} label="Department" value={detail.document.department} />
                  <Meta icon={User} label="Owner" value={detail.document.owner_name} />
                  <Meta icon={ShieldCheck} label="Effective" value={detail.document.effective_date ? formatDate(detail.document.effective_date) : null} />
                  <Meta icon={Clock} label="Review due" value={detail.document.review_due_date ? formatDate(detail.document.review_due_date) : null} />
                  <Meta icon={History} label="Current" value={`v${detail.document.current_version_no}`} />
                </div>
                {detail.document.tags?.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Tag className="w-3.5 h-3.5 text-[#8A857A]" />
                    {detail.document.tags.map((t) => <span key={t} className="text-[11px] bg-[#F4F2ED] border border-[#E6E2D9] rounded-full px-2 py-0.5 text-[#59554D]">{t}</span>)}
                  </div>
                )}

                {/* Status control */}
                {detail.can_manage && (
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] uppercase tracking-wide text-[#8A857A] font-semibold">Status</span>
                    <Select value={detail.document.status} onValueChange={(v) => changeStatus(detail.document, v)}>
                      <SelectTrigger className="h-8 w-[180px] bg-white border-[#DAD5CA] text-sm" data-testid="doc-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}

                {/* Access */}
                {detail.can_manage && (
                  <div className="rounded-lg border border-[#E6E2D9] bg-white p-3" data-testid="doc-detail-access">
                    <div className="text-[11px] font-bold uppercase tracking-wide text-[#8A857A] mb-1.5 flex items-center gap-1.5">
                      {detail.document.restricted ? <Lock className="w-3.5 h-3.5 text-[#B8860B]" /> : <User className="w-3.5 h-3.5" />} Access
                    </div>
                    {!detail.document.restricted ? (
                      <p className="text-[13px] text-[#59554D]">Visible to <span className="font-semibold text-[#1A1A1A]">all staff</span> (published).</p>
                    ) : (
                      <div className="text-[13px] text-[#59554D] space-y-1">
                        <p>Admins & managers, plus:</p>
                        {(detail.document.allowed_roles || []).length > 0 && (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {detail.document.allowed_roles.map((r) => <span key={r} className="text-[11px] bg-[#F0ECE1] border border-[#E2DCCF] rounded-full px-2 py-0.5 text-[#59554D] capitalize">{r.replace("_", " ")}s</span>)}
                          </div>
                        )}
                        {(detail.document.allowed_users || []).length > 0 && (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {detail.document.allowed_users.map((u) => <span key={u} className="text-[11px] bg-[#FFF6DA] border border-[#EBD9A0] rounded-full px-2 py-0.5 text-[#8A6D00]">{u}</span>)}
                          </div>
                        )}
                        {(detail.document.allowed_roles || []).length === 0 && (detail.document.allowed_users || []).length === 0 && (
                          <p className="text-[12px] text-[#A39D8F]">No one else — managers only.</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Version history */}
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wide text-[#8A6D00] mb-1.5 flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Version history</div>
                  <div className="rounded-lg border border-[#E6E2D9] bg-white overflow-hidden">
                    {(detail.versions || []).map((v) => (
                      <div key={v.id} className="flex items-center justify-between px-3 py-2 border-b border-[#F0EDE6] text-sm" data-testid={`doc-version-${v.version_no}`}>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="mono text-[12px] font-semibold text-[#1A1A1A]">v{v.version_no}</span>
                            {v.version_no === detail.document.current_version_no && <span className="text-[9px] uppercase font-bold text-[#0E6B3F] bg-[#E9F6EE] rounded px-1.5 py-0.5">Current</span>}
                            <span className="text-[11px] text-[#8A857A] truncate">{v.filename} · {bytes(v.size)}</span>
                          </div>
                          <div className="text-[11px] text-[#8A857A]">{v.uploaded_by} · {v.uploaded_at ? formatDate(v.uploaded_at) : ""}{v.change_note ? ` · ${v.change_note}` : ""}</div>
                        </div>
                        <button onClick={() => download(detail.document, v.version_no)} title="Download this version" className="w-7 h-7 shrink-0 rounded-md flex items-center justify-center text-[#6B6660] hover:bg-[#F4F2ED] hover:text-[#1A1A1A]" data-testid={`doc-version-download-${v.version_no}`}><Download className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Audit */}
                {detail.can_manage && detail.audit?.length > 0 && (
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wide text-[#8A857A] mb-1.5">Activity</div>
                    <div className="space-y-1">
                      {detail.audit.map((a) => (
                        <div key={a.id} className="text-[11.5px] text-[#59554D] flex items-center gap-2">
                          <span className="text-[#8A857A]">{a.at ? formatDate(a.at) : ""}</span>
                          <span className="font-medium text-[#1A1A1A]">{a.action.replace(/_/g, " ")}</span>
                          <span className="text-[#8A857A]">· {a.by}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* New / Edit dialog */}
      <DocFormDialog
        open={formOpen}
        editDoc={editDoc}
        cats={cats}
        depts={depts}
        onClose={() => { setFormOpen(false); setEditDoc(null); }}
        onSaved={(d) => { load(); if (detail && d && detail.document.id === d.id) refreshDetail(d.id); }}
      />

      {/* New version dialog */}
      <NewVersionDialog
        doc={versionFor}
        onClose={() => setVersionFor(null)}
        onSaved={(d) => { load(); refreshDetail(d.id); }}
      />

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o && !deleting) setDeleteTarget(null); }}>
        <DialogContent className="bg-white border-[#DAD5CA] text-[#1A1A1A] max-w-md" data-testid="doc-delete-confirm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Trash2 className="w-4 h-4 text-[#C23A00]" /> Delete this document?</DialogTitle></DialogHeader>
          {deleteTarget && <p className="text-sm text-[#59554D]"><span className="mono font-semibold text-[#1A1A1A]">{deleteTarget.doc_ref}</span> — {deleteTarget.title} and all its versions will be removed from the registry. This can’t be undone.</p>}
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting} className="text-[#59554D] hover:text-[#1A1A1A]" data-testid="doc-delete-cancel">Cancel</Button>
            <Button onClick={del} disabled={deleting} className="bg-[#C23A00] hover:bg-[#A83100] text-white" data-testid="doc-delete-confirm-btn">{deleting && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />} Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-[#8A857A] mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-[#8A857A] font-semibold">{label}</div>
        <div className="text-[13px] text-[#1A1A1A] truncate">{value || "—"}</div>
      </div>
    </div>
  );
}

// ── New / Edit document ──
function DocFormDialog({ open, editDoc, cats = [], depts = [], onClose, onSaved }) {
  const CATEGORIES = cats;
  const DEPARTMENTS = depts;
  const isEdit = !!editDoc;
  const [form, setForm] = useState({});
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [reps, setReps] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/agents/mapping`)
      .then((r) => setReps((r.data.agents || []).filter((a) => a.id !== "emergency").map((a) => a.name).filter(Boolean)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setForm(isEdit ? {
      title: editDoc.title || "", category: editDoc.category || "policy", department: editDoc.department || "",
      owner_name: editDoc.owner_name || "", description: editDoc.description || "",
      tags: (editDoc.tags || []).join(", "), effective_date: editDoc.effective_date || "",
      review_due_date: editDoc.review_due_date || "", status: editDoc.status || "published",
      restricted: !!editDoc.restricted, allowed_roles: editDoc.allowed_roles || [], allowed_users: editDoc.allowed_users || [],
    } : {
      title: "", category: "policy", department: "", owner_name: "", description: "",
      tags: "", effective_date: "", review_due_date: "", status: "published",
      restricted: false, allowed_roles: [], allowed_users: [],
    });
  }, [open, editDoc, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleRole = (r) => setForm((f) => ({ ...f, allowed_roles: (f.allowed_roles || []).includes(r) ? f.allowed_roles.filter((x) => x !== r) : [...(f.allowed_roles || []), r] }));
  const toggleUser = (n) => setForm((f) => ({ ...f, allowed_users: (f.allowed_users || []).includes(n) ? f.allowed_users.filter((x) => x !== n) : [...(f.allowed_users || []), n] }));

  const submit = async () => {
    if (!form.title?.trim()) { toast.error("Title is required"); return; }
    if (!isEdit && !file) { toast.error("Please choose a file to upload"); return; }
    setProgress(0);
    setSaving(true);
    try {
      if (isEdit) {
        const payload = {
          title: form.title, category: form.category, department: form.department,
          owner_name: form.owner_name, description: form.description,
          tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
          effective_date: form.effective_date || null, review_due_date: form.review_due_date || null,
          status: form.status,
          restricted: !!form.restricted, allowed_roles: form.restricted ? (form.allowed_roles || []) : [],
          allowed_users: form.restricted ? (form.allowed_users || []) : [],
        };
        const r = await axios.put(`${API}/documents/${editDoc.id}`, payload);
        toast.success("Document updated");
        onSaved(r.data.document);
      } else {
        const fd = new FormData();
        fd.append("file", file);
        ["title", "category", "department", "owner_name", "description", "tags", "effective_date", "review_due_date", "status"].forEach((k) => fd.append(k, form[k] || ""));
        fd.append("restricted", form.restricted ? "true" : "false");
        fd.append("allowed_roles", form.restricted ? (form.allowed_roles || []).join(",") : "");
        fd.append("allowed_users", form.restricted ? (form.allowed_users || []).join(",") : "");
        const r = await axios.post(`${API}/documents`, fd, {
          onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded * 100) / e.total)); },
        });
        toast.success("Document added");
        onSaved(r.data.document);
      }
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); setProgress(0); }
  };

  const deptOptions = form.department && !DEPARTMENTS.includes(form.department) ? [form.department, ...DEPARTMENTS] : DEPARTMENTS;
  const ownerOptions = form.owner_name && !reps.includes(form.owner_name) ? [form.owner_name, ...reps] : reps;

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <SheetContent side="right" className="bg-[#FBFAF7] border-[#DAD5CA] w-full sm:max-w-lg overflow-y-auto flex flex-col p-0" data-testid="doc-form-sheet">
        <SheetHeader className="px-6 py-4 border-b border-[#E6E2D9] bg-white">
          <SheetTitle>{isEdit ? "Edit document" : "New document"}</SheetTitle>
        </SheetHeader>
        <div className="px-6 py-4 space-y-3 flex-1">
          {!isEdit && (
            <Field label="File *">
              <input ref={fileRef} type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm text-[#59554D] file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-[#DAD5CA] file:bg-[#F4F2ED] file:text-[#1A1A1A] file:text-xs file:font-semibold hover:file:bg-[#EBE8E1]" data-testid="doc-form-file" />
              {file && <p className="text-[11px] text-[#8A857A] mt-1">{file.name} · {bytes(file.size)}</p>}
            </Field>
          )}
          <Field label="Title *"><Input value={form.title || ""} onChange={(e) => set("title", e.target.value)} className="bg-white border-[#DAD5CA]" data-testid="doc-form-title" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="bg-white border-[#DAD5CA]" data-testid="doc-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="bg-white border-[#DAD5CA]" data-testid="doc-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Department">
              <Select value={form.department || undefined} onValueChange={(v) => set("department", v)}>
                <SelectTrigger className="bg-white border-[#DAD5CA]" data-testid="doc-form-department"><SelectValue placeholder="Select department" /></SelectTrigger>
                <SelectContent>{deptOptions.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Owner">
              <Select value={form.owner_name || undefined} onValueChange={(v) => set("owner_name", v)}>
                <SelectTrigger className="bg-white border-[#DAD5CA]" data-testid="doc-form-owner"><SelectValue placeholder="Select owner" /></SelectTrigger>
                <SelectContent>{ownerOptions.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Effective date"><Input type="date" value={form.effective_date || ""} onChange={(e) => set("effective_date", e.target.value)} className="bg-white border-[#DAD5CA]" data-testid="doc-form-effective" /></Field>
            <Field label="Review due date"><Input type="date" value={form.review_due_date || ""} onChange={(e) => set("review_due_date", e.target.value)} className="bg-white border-[#DAD5CA]" data-testid="doc-form-review" /></Field>
          </div>
          <Field label="Tags (comma separated)"><Input value={form.tags || ""} onChange={(e) => set("tags", e.target.value)} placeholder="hr, safety, whs" className="bg-white border-[#DAD5CA]" data-testid="doc-form-tags" /></Field>
          <Field label="Description"><Textarea value={form.description || ""} onChange={(e) => set("description", e.target.value)} rows={3} className="bg-white border-[#DAD5CA]" data-testid="doc-form-description" /></Field>

          {/* Restrict access */}
          <div className="rounded-lg border border-[#E6E2D9] bg-[#FBFAF7] p-3">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input type="checkbox" checked={!!form.restricted} onChange={(e) => set("restricted", e.target.checked)} className="w-4 h-4 accent-[#B8860B]" data-testid="doc-form-restricted-toggle" />
              <div>
                <div className="flex items-center gap-1.5 text-[13px] font-semibold text-[#1A1A1A]"><Lock className="w-3.5 h-3.5 text-[#B8860B]" /> Restrict access</div>
                <div className="text-[11px] text-[#8A857A]">By default everyone can see published documents. Turn on to limit who can view this one.</div>
              </div>
            </label>
            {form.restricted && (
              <div className="mt-3 space-y-3 pl-1" data-testid="doc-form-restrict-panel">
                <p className="text-[11px] text-[#8A857A]">Admins & managers always retain access. Choose who else can view:</p>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-[#8A857A] font-semibold mb-1.5">Roles</div>
                  <div className="flex gap-2 flex-wrap">
                    {[{ id: "team_lead", label: "Team Leads" }, { id: "agent", label: "All Agents" }].map((r) => {
                      const on = (form.allowed_roles || []).includes(r.id);
                      return <button key={r.id} type="button" onClick={() => toggleRole(r.id)} className={`h-7 px-3 rounded-full text-[11px] font-semibold transition-colors ${on ? "bg-[#1A1A1A] text-white" : "bg-white border border-[#DAD5CA] text-[#59554D] hover:bg-[#F0ECE1]"}`} data-testid={`doc-form-role-${r.id}`}>{on ? "✓ " : ""}{r.label}</button>;
                    })}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-[#8A857A] font-semibold mb-1.5">Specific people {(form.allowed_users || []).length > 0 && <span className="text-[#A39D8F]">({form.allowed_users.length})</span>}</div>
                  <div className="max-h-[150px] overflow-y-auto rounded-md border border-[#E6E2D9] bg-white divide-y divide-[#F0EDE6]">
                    {reps.length === 0 ? <div className="px-3 py-2 text-[12px] text-[#8A857A]">No users found</div> : reps.map((n) => {
                      const on = (form.allowed_users || []).includes(n);
                      return (
                        <label key={n} className="flex items-center gap-2.5 px-3 py-1.5 cursor-pointer hover:bg-[#FBFAF7]" data-testid={`doc-form-user-${n.replace(/\s+/g, '-')}`}>
                          <input type="checkbox" checked={on} onChange={() => toggleUser(n)} className="w-3.5 h-3.5 accent-[#B8860B]" />
                          <span className="text-[13px] text-[#1A1A1A]">{n}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#E6E2D9] bg-white flex items-center justify-end gap-2">
          {saving && !isEdit && (
            <div className="flex-1 mr-1" data-testid="doc-upload-progress">
              <div className="h-1.5 rounded-full bg-[#EDEAE3] overflow-hidden">
                <div className="h-full bg-[#FFD108] transition-all duration-150" style={{ width: `${progress}%` }} />
              </div>
              <div className="text-[10px] text-[#8A857A] mt-1">{progress < 100 ? `Uploading… ${progress}%` : "Processing…"}</div>
            </div>
          )}
          <Button variant="ghost" onClick={onClose} disabled={saving} className="text-[#59554D]" data-testid="doc-form-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A]" data-testid="doc-form-save">{saving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />} {isEdit ? "Save changes" : "Add document"}</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── Upload new version ──
function NewVersionDialog({ doc, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  useEffect(() => { if (doc) { setFile(null); setNote(""); } }, [doc]);

  const submit = async () => {
    if (!file) { toast.error("Please choose a file"); return; }
    setProgress(0);
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("change_note", note || "");
      const r = await axios.post(`${API}/documents/${doc.id}/versions`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded * 100) / e.total)); },
      });
      toast.success(`Version v${r.data.version.version_no} uploaded`);
      onSaved(r.data.document);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to upload"); }
    finally { setSaving(false); setProgress(0); }
  };

  return (
    <Dialog open={!!doc} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="bg-white border-[#DAD5CA] text-[#1A1A1A] max-w-md" data-testid="doc-version-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Upload className="w-4 h-4 text-[#8A6D00]" /> Upload new version</DialogTitle></DialogHeader>
        {doc && (
          <div className="space-y-3">
            <p className="text-sm text-[#59554D]"><span className="mono font-semibold text-[#1A1A1A]">{doc.doc_ref}</span> — {doc.title}. This becomes <span className="font-semibold">v{(doc.current_version_no || 1) + 1}</span> (current); previous versions stay available.</p>
            <Field label="File *"><input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm text-[#59554D] file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-[#DAD5CA] file:bg-[#F4F2ED] file:text-[#1A1A1A] file:text-xs file:font-semibold hover:file:bg-[#EBE8E1]" data-testid="doc-version-file" />{file && <p className="text-[11px] text-[#8A857A] mt-1">{file.name} · {bytes(file.size)}</p>}</Field>
            <Field label="What changed?"><Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="e.g. Updated section 4, annual review" className="bg-white border-[#DAD5CA]" data-testid="doc-version-note" /></Field>
          </div>
        )}
        <div className="flex items-center justify-end gap-2 mt-1">
          {saving && (
            <div className="flex-1 mr-1" data-testid="doc-version-progress">
              <div className="h-1.5 rounded-full bg-[#EDEAE3] overflow-hidden">
                <div className="h-full bg-[#FFD108] transition-all duration-150" style={{ width: `${progress}%` }} />
              </div>
              <div className="text-[10px] text-[#8A857A] mt-1">{progress < 100 ? `Uploading… ${progress}%` : "Processing…"}</div>
            </div>
          )}
          <Button variant="ghost" onClick={onClose} disabled={saving} className="text-[#59554D]" data-testid="doc-version-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A]" data-testid="doc-version-save">{saving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />} Upload version</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wide text-[#8A857A] font-semibold block mb-1">{label}</label>
      {children}
    </div>
  );
}
